import React, { useState } from "react";
import styled from "styled-components";
import Colors from "../../../styles/Colors";
import { H4 } from "../../../styles/Fonts";
import LabelWithHint from "../../../components/LabelWithHint";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faAngleUp,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { openNewTab, getTruncNum } from "../../../utils/common";
import { useAppSelector } from "../../../hooks";
import { PairProps } from "../../../utils/constant/pairs";
import { UserClosedPositionProps } from "../../../redux/factorySlice";
import { networks } from "../../../utils/networks";
import PairTokenIcon from "../../../components/PairTokenIcon";

const ClosedPosition: React.FC<{
  pair: PairProps;
  position: UserClosedPositionProps;
}> = (props) => {
  const { position, pair } = props;
  const {
    positionId,
    startWantAmount,
    reserveAmount,
    endWantAmount,
    closePrice,
    eventName,
    startPrice,
    startTime,
    closeTime,
    upperTickPrice,
    lowerTickPrice,
    borrowRatio,
    reserveWantAmount,
  } = position;
  const { wantToken, borrowToken, factoryAddress, deployedChainId } = pair;
  const { tokenDecimal, tokenSymbol } = wantToken;
  const { tokenSymbol: borrowTokenSymbol } = borrowToken;

  const positionInfo = useAppSelector(
    (state) => state.factory.positionInfos?.[factoryAddress]?.[positionId]
  );

  const [isShow, setIsShow] = useState<boolean>(false);

  const openUniPage = (_positionId: number) => {
    openNewTab(
      `https://app.uniswap.org/#/pool/${_positionId}?chain=${networks[deployedChainId].label}`
    );
  };

  return (
    <Container>
      <Layer1Container>
        <ValueContainer>
          <Layer1Col>
            <LabelContainer>
              <HeadText>Pair</HeadText>
            </LabelContainer>
            <PairNameRow>
              <PairTokenIcon
                borrowTokenIcon={pair.borrowToken.tokenIcon}
                wantTokenIcon={pair.wantToken.tokenIcon}
              />
              <ValueText>{pair.label}</ValueText>
            </PairNameRow>
          </Layer1Col>
          <Layer1Col>
            <LabelContainer>
              <LabelWithHint
                labelComponent={<HeadText>Event</HeadText>}
                hintText=""
              />
            </LabelContainer>
            <ValueText>{eventName}</ValueText>
          </Layer1Col>
          <Layer1Col>
            <LabelContainer>
              <LabelWithHint
                labelComponent={<HeadText>Open Amount</HeadText>}
                hintText="The initial investment you put in this position"
              />
            </LabelContainer>
            <ValueText>
              {`${getTruncNum(+startWantAmount, 5)} ${tokenSymbol}`}
            </ValueText>
          </Layer1Col>
          <Layer1Col>
            <LabelContainer>
              <LabelWithHint
                labelComponent={<HeadText>Close Amount</HeadText>}
                hintText="The closed value of this position"
              />
            </LabelContainer>
            <ValueText>
              {`${getTruncNum(+endWantAmount, 5)} ${tokenSymbol}`}
            </ValueText>
          </Layer1Col>
        </ValueContainer>
        <BtnContainer>
          <Arrow
            icon={isShow ? faAngleUp : faAngleDown}
            color={Colors.ivory}
            onClick={() => setIsShow((pre) => !pre)}
          />
        </BtnContainer>
      </Layer1Container>
      <Layer2Container isShow={isShow}>
        <Layer2Row>
          <Layer2Col>
            <Row>
              <LabelWithHint
                labelComponent={<Layer2Label>Position Id</Layer2Label>}
                hintText="The NFT id of your position"
              />

              <Icon
                icon={faArrowUpRightFromSquare}
                onClick={() => {
                  openUniPage(positionId);
                }}
              />
            </Row>
            <Layer2Value>{positionId}</Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>PnL</Layer2Label>}
              hintText="Close Amount - (Open Amount + Extra Collateral)"
            />
            <Layer2Value>
              {`${getTruncNum(
                +endWantAmount -
                  +startWantAmount -
                  (+reserveAmount - +reserveWantAmount),
                5
              )} ${tokenSymbol}`}
            </Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Start Price</Layer2Label>}
              hintText=""
            />
            <Layer2Value>{`${getTruncNum(
              +startPrice,
              5
            )} ${tokenSymbol} per ${borrowTokenSymbol}`}</Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Close Price</Layer2Label>}
              hintText=""
            />
            <Layer2Value>{`${getTruncNum(
              +closePrice,
              5
            )} ${tokenSymbol} per ${borrowTokenSymbol}`}</Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Start Time</Layer2Label>}
              hintText=""
            />
            <Layer2Value>
              {new Date(+startTime * 1000).toLocaleString("en")}
            </Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Closed Time</Layer2Label>}
              hintText=""
            />
            <Layer2Value>
              {new Date(closeTime * 1000).toLocaleString("en")}
            </Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Range</Layer2Label>}
              hintText=""
            />
            <Layer2Value>
              {lowerTickPrice && upperTickPrice
                ? `${getTruncNum(+lowerTickPrice, 5)} - ${getTruncNum(
                    +upperTickPrice,
                    5
                  )} ${wantToken.tokenSymbol} per ${borrowToken.tokenSymbol}`
                : "-"}
            </Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Leverage</Layer2Label>}
              hintText=""
            />
            <Layer2Value>{+borrowRatio + 1}X</Layer2Value>
          </Layer2Col>
        </Layer2Row>
      </Layer2Container>
    </Container>
  );
};

export default ClosedPosition;

const Container = styled.div``;

const LabelContainer = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    flex: initial;
  }
`;

const HeadText = styled(H4)`
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: ${Colors.gray1};
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
  }
`;

const ValueText = styled(H4)`
  flex: 1 1 200px;
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: ${Colors.gray1};
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
  }
  @media (max-width: 768px) {
    flex: initial;
  }
`;

const Layer1Container = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 0;
  button {
    border-radius: 4px;
    font-size: 13px;
    line-height: 18px;
    padding: 4px 8px;
    width: auto;
    margin: auto;
    @media (max-width: 768px) {
      font-size: 12px;
      line-height: 16px;
    }
  }
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 12px 0;
  }
`;

const ValueContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const Layer1Col = styled.div`
  flex: 1 1 200px;
  padding: 4px 0;
  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    flex: inherit;
    justify-content: space-between;
  }
`;

const Layer2Container = styled.div<{ isShow: boolean }>`
  max-height: ${(props) => (props.isShow ? "600px" : "0px")};
  transition: all ease 300ms;
  overflow: hidden;
`;

const Layer2Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  background-color: ${Colors.backgroundGray4};
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const Layer2Col = styled.div`
  width: 25%;
  padding: 6px 4px;
  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
`;

const Layer2Label = styled(H4)`
  font-size: 13px;
  line-height: 22px;
  font-weight: 500;
  color: ${Colors.gray1};
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
  }
`;

const Layer2Value = styled(H4)`
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: ${Colors.gray1};
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
    text-align: right;
  }
`;

const BtnContainer = styled.div`
  flex: 0 0 40px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  @media (max-width: 768px) {
    width: 100%;
    flex: initial;
    margin: 6px 0;
  }
`;

const Arrow = styled(FontAwesomeIcon)`
  padding: 4px 8px;
  cursor: pointer;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const Icon = styled(FontAwesomeIcon)`
  margin-left: 8px;
  color: ${Colors.ivory};
  font-size: 12px;
  cursor: pointer;
`;

const PairNameRow = styled.div`
  display: flex;
  align-items: center;
`;
